// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    Alert,
    FormField,
    Text,
    Translate,
    useTranslate,
} from '@plesk/ui-library';
import { Dialog } from 'common/components/Dialog/Dialog';
import { Loader } from 'common/components';
import AsyncSelectInput from 'common/components/Select/AsyncSelectInput';
import { createOptionsLoader } from 'common/components/Select/helpers';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import { HTTP_CODES } from 'common/api/constants';
import { IUserResponse } from 'common/api/resources/User';
import {
    IMassMailingPreviewResponse,
    IMassMailUsersRequest,
} from 'common/api/resources/MassMailing';
import { MASS_MAILING_FORM } from 'admin/massMailing/constants';
import {
    PreviewIframe,
    PreviewRecipientRow,
    PreviewSubject,
} from 'admin/massMailing/containers/Styles';
import * as massMailingActions from 'admin/massMailing/actions';
import * as usersActions from 'admin/user/actions';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import { connect } from 'react-redux';
import { RootState } from 'admin/core/store';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';

interface IPreviewRecipientOption {
    label: string;
    value?: number;
}

export interface IPreviewDialogOwnProps {
    isOpen: boolean;
    values: IMassMailUsersRequest;
    onClose: () => void;
}

export type PreviewDialogProps =
    IPreviewDialogOwnProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const PreviewDialog: React.FC<PreviewDialogProps> = ({
    isOpen,
    isLoading,
    values,
    onClose,
    previewMassMailing,
    getUsers,
}) => {
    const translate = useTranslate();
    const [previewRecipientOption, setPreviewRecipientOption] = React.useState<IPreviewRecipientOption | null>(null);
    const [previewResult, setPreviewResult] = React.useState<IMassMailingPreviewResponse | null>(null);
    const [previewErrorMessage, setPreviewErrorMessage] = React.useState('');

    // The dialog only ever has one preview request in flight: a new trigger while one is
    // still pending is ignored rather than left to race with it.
    const isFetchingRef = React.useRef(false);

    const previewRecipientLoader = createOptionsLoader(getUsers, (user: IUserResponse) => ({
        label: user.email,
        value: user.id,
    }));

    const fetchPreview = async (userId?: number) => {
        if (isFetchingRef.current) {
            return;
        }

        isFetchingRef.current = true;
        setPreviewErrorMessage('');

        try {
            const result = await previewMassMailing({
                ...values,
                user_id: userId,
            });

            if (result) {
                setPreviewResult(result);

                if (!result.is_sample) {
                    setPreviewRecipientOption({ label: result.recipient.email, value: result.recipient.id ?? undefined });
                }
            }
        } catch (e) {
            setPreviewResult(null);

            if (!e.response) {
                setPreviewErrorMessage(e.message);
                return;
            }

            if (e.response.status === HTTP_CODES.VALIDATION_ERROR) {
                const errors = e.response.data.errors;

                setPreviewErrorMessage(
                    (errors && (errors.message || errors.subject || errors.user_id)) || e.response.data.message
                );
                return;
            }

            if (e.response.status >= HTTP_CODES.BAD_REQUEST) {
                setPreviewErrorMessage(e.response.data.message);
            }
        } finally {
            isFetchingRef.current = false;
        }
    };

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        setPreviewRecipientOption(null);
        setPreviewResult(null);
        fetchPreview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handlePreviewRecipientChange = (option: IPreviewRecipientOption | null) => {
        setPreviewRecipientOption(option);
        fetchPreview(option?.value);
    };

    return (
        <Dialog
            heading={<Translate content="massMailing.form.previewDialogTitle" />}
            closeHandler={onClose}
            isOpen={isOpen}
            size={SIZE.LG}
            data-cy={MASS_MAILING_FORM.PREVIEW_DIALOG}
        >
            <Loader isLoading={isLoading}>
                <PreviewRecipientRow>
                    <FormField
                        name="preview_recipient"
                        label={<Translate content="massMailing.form.previewRecipient" />}
                    >
                        {({ getId }) => (
                            <AsyncSelectInput
                                inputId={getId()}
                                value={previewRecipientOption}
                                isMulti={false}
                                isClearable={true}
                                loadOptions={previewRecipientLoader}
                                onChange={handlePreviewRecipientChange}
                                debounceTimeout={1000}
                                additional={{ page: 1 }}
                                menuPosition="fixed"
                            />
                        )}
                    </FormField>
                    <Text
                        fontSize={SIZE.SM}
                        intent={INTENT_TYPE.MUTED}
                    >
                        <Translate content="massMailing.form.previewRecipientDescription" />
                    </Text>
                </PreviewRecipientRow>
                {previewErrorMessage && (
                    <Alert intent={INTENT_TYPE.DANGER} data-cy={MASS_MAILING_FORM.PREVIEW_ERROR}>
                        {previewErrorMessage}
                    </Alert>
                )}
                {previewResult && (
                    <>
                        {previewResult.is_sample && (
                            <Alert intent={INTENT_TYPE.WARNING} data-cy={MASS_MAILING_FORM.PREVIEW_SAMPLE_LABEL}>
                                <Translate content="massMailing.form.previewSampleData" />
                            </Alert>
                        )}
                        <Text component="div" bold={true}>
                            <Translate content="massMailing.form.previewSubjectLabel" />
                        </Text>
                        <PreviewSubject data-cy={MASS_MAILING_FORM.PREVIEW_SUBJECT}>
                            {previewResult.subject}
                        </PreviewSubject>
                        <Text component="div" bold={true}>
                            <Translate content="massMailing.form.previewBodyLabel" />
                        </Text>
                        <PreviewIframe
                            sandbox=""
                            srcDoc={previewResult.body}
                            data-cy={MASS_MAILING_FORM.PREVIEW_IFRAME}
                            title={translate('massMailing.form.previewDialogTitle').toString()}
                        />
                    </>
                )}
            </Loader>
        </Dialog>
    );
};

const mapStateToProps = (state: RootState) => ({
    isLoading: state.app.loadingFlags.has(LOADING_FLAGS.MASS_MAIL_PREVIEWING),
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
    previewMassMailing: massMailingActions.previewMassMailing,
    getUsers: usersActions.getUsers,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(PreviewDialog);
